import React from 'react';
import { BookOpen, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import useSmartBack from './useSmartBack';

const isMyBookieDomain = () => {
  const hostname = window.location.hostname;
  return !hostname.includes('repti-sitter') && !hostname.includes('reptisitter');
};

const getHomePath = () => isMyBookieDomain() ? '/' : '/mybookie';

const MyBookiePrivacy = () => {
  const navigate = useNavigate();
  const goBack = useSmartBack(getHomePath());
  
  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      {/* Header */}
      <nav className="bg-white border-b border-[#E6E8E6] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer" 
            onClick={() => navigate(getHomePath())}
          >
            <div className="w-10 h-10 bg-[#0A2B1D] rounded-sm flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-[#111614] tracking-tight">MyBookie</span>
          </div>
          <Button 
            variant="ghost" 
            onClick={goBack}
            className="text-[#5C6661] hover:text-[#111614]"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white border border-[#E6E8E6] p-8 md:p-12">
          <h1 className="text-3xl font-semibold text-[#111614] mb-2">Privacy Policy</h1>
          <p className="text-[#5C6661] mb-8">Last updated: February 2026</p>

          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">1. Introduction</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                MyBookie ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our bookkeeping and expense tracking service.
              </p>
              <p className="text-[#5C6661] leading-relaxed">
                By using MyBookie, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-medium text-[#111614] mb-3">2.1 Personal Information</h3>
              <ul className="list-disc list-inside text-[#5C6661] mb-4 space-y-2">
                <li>Name and email address when you create an account</li>
                <li>Password (stored in encrypted form)</li>
                <li>Business information you provide (business name, type, description)</li>
              </ul>

              <h3 className="text-lg font-medium text-[#111614] mb-3">2.2 Financial Information</h3>
              <ul className="list-disc list-inside text-[#5C6661] mb-4 space-y-2">
                <li>Bank account information accessed through Plaid (account names, balances, transactions)</li>
                <li>Expense and income records you manually enter</li>
                <li>Transaction categories and descriptions</li>
              </ul>

              <h3 className="text-lg font-medium text-[#111614] mb-3">2.3 Automatically Collected Information</h3>
              <ul className="list-disc list-inside text-[#5C6661] space-y-2">
                <li>Device information (browser type, operating system)</li>
                <li>Usage data (features used, time spent)</li>
                <li>IP address and general location</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">3. How We Use Your Information</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">We use the information we collect to:</p>
              <ul className="list-disc list-inside text-[#5C6661] space-y-2">
                <li>Provide, maintain, and improve our services</li>
                <li>Import and categorize your financial transactions</li>
                <li>Generate financial reports and cash flow forecasts</li>
                <li>Provide AI-powered financial insights through FinanceBot</li>
                <li>Send you important updates about your account</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Detect and prevent fraud or unauthorized access</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">4. Bank Connection Security (Plaid)</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                We use Plaid Inc. to securely connect your bank accounts. When you connect a bank account:
              </p>
              <ul className="list-disc list-inside text-[#5C6661] space-y-2">
                <li>Your bank credentials are never stored on our servers</li>
                <li>Plaid uses bank-level encryption (256-bit) to protect your data</li>
                <li>We only receive read-only access to transaction data</li>
                <li>You can disconnect your bank at any time</li>
              </ul>
              <p className="text-[#5C6661] leading-relaxed mt-4">
                For more information, please review <a href="https://plaid.com/legal/" target="_blank" rel="noopener noreferrer" className="text-[#0A2B1D] underline">Plaid's Privacy Policy</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                We do not sell your personal information. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-[#5C6661] space-y-2">
                <li><strong>Service Providers:</strong> With third-party vendors who help us operate our service (e.g., Plaid for bank connections, cloud hosting providers)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to respond to legal process</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information (e.g., sharing reports with your accountant)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">6. Data Security</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc list-inside text-[#5C6661] space-y-2">
                <li>256-bit SSL/TLS encryption for data in transit</li>
                <li>Encrypted storage for sensitive data at rest</li>
                <li>Regular security audits and monitoring</li>
                <li>Secure password hashing using bcrypt</li>
                <li>Access controls and authentication requirements</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">7. Data Retention</h2>
              <p className="text-[#5C6661] leading-relaxed">
                We retain your data for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time by contacting us. Some data may be retained as required by law or for legitimate business purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">8. Your Rights</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">You have the right to:</p>
              <ul className="list-disc list-inside text-[#5C6661] space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Export your data in a portable format</li>
                <li>Disconnect linked bank accounts at any time</li>
                <li>Opt out of marketing communications</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">9. Children's Privacy</h2>
              <p className="text-[#5C6661] leading-relaxed">
                MyBookie is not intended for use by children under 18. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">10. Changes to This Policy</h2>
              <p className="text-[#5C6661] leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. Continued use of MyBookie after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-medium text-[#111614] mb-4">11. Contact Us</h2>
              <p className="text-[#5C6661] leading-relaxed">
                If you have questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="text-[#0A2B1D] mt-4">
                <strong>Email:</strong> privacy@mybookie.ai
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#111614] text-[#8A948F] py-8 px-6">
        <div className="max-w-4xl mx-auto text-center text-sm">
          © 2026 MyBookie. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default MyBookiePrivacy;
